import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Scale, Gavel, Landmark, PawPrint } from "lucide-react";
import { api, formatDate } from "../lib/api";
import { SITE, IMAGES } from "../config/site";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, MaskedLine, SectionHeading } from "../components/Reveal";

const AREA_ICONS = [Scale, Gavel, Landmark, PawPrint];

export default function HomePage() {
  const { t, lang } = useLanguage();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles", { params: { lang } }).then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
  }, [lang]);

  const areas = t("practice.areas");
  const credentials = t("credentials.items");

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-svh flex items-center pt-28 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.08] text-ebony" data-testid="hero-title">
              <MaskedLine delay={0.15}>{t("hero.line1")}</MaskedLine>
              <MaskedLine delay={0.3}>{t("hero.line2a")} <em className="italic text-forest">{t("hero.line2b")}</em></MaskedLine>
              <MaskedLine delay={0.45}><em className="italic text-forest">{t("hero.line3")}</em></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-base sm:text-lg text-[#4D5248] leading-relaxed max-w-xl mt-8"
              data-testid="hero-positioning"
            >
              {t("hero.positioning")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap gap-4 mt-10"
            >
              <Link to="/agendamento" data-testid="hero-cta-booking-button" className="btn-primary w-full sm:w-auto">
                {t("nav.cta")} <ArrowRight size={14} />
              </Link>
              <Link to="/sobre" data-testid="hero-cta-about-button" className="btn-outline w-full sm:w-auto">
                {t("nav.about")}
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -top-4 -right-4 w-full h-full border border-gold/60 pointer-events-none" />
              <motion.div style={{ y: portraitY }} className="relative overflow-hidden aspect-[4/5] bg-sage">
                <img
                  src={IMAGES.portrait}
                  alt={`${SITE.name}, ${t("site.title")}`}
                  className="w-full h-full object-cover scale-105"
                  data-testid="hero-portrait"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ebony/30 via-transparent to-transparent" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ÁREAS DE ATUAÇÃO */}
      <section className="py-16 sm:py-24 lg:py-32" data-testid="practice-areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-5">
              <SectionHeading overline={t("practice.overline")} title={t("practice.title")} />
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <Reveal delay={0.15}>
                <p className="text-base text-[#4D5248] leading-relaxed">
                  {t("practice.intro")}
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E2E4DC] border border-[#E2E4DC]">
            {areas.map((area, i) => {
              const Icon = AREA_ICONS[i];
              return (
                <Reveal key={area.n} delay={0.08 * i} className="h-full">
                  <div
                    data-testid={`practice-area-card-${area.n}`}
                    className="group bg-alabaster hover:bg-white p-8 sm:p-10 h-full transition-colors duration-500 relative"
                  >
                    <div className="flex items-start justify-between mb-10">
                      <span className="font-mono text-xs text-gold tracking-[0.2em]">{area.n}</span>
                      <Icon size={22} strokeWidth={1.5} className="text-olive group-hover:text-forest transition-colors duration-500" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-serif font-medium text-ebony mb-4 group-hover:text-forest transition-colors duration-500">
                      {area.title}
                    </h3>
                    <p className="text-sm text-[#4D5248] leading-relaxed">{area.text}</p>
                    <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-forest group-hover:w-full transition-[width] duration-700" />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CREDENCIAIS */}
      <section className="py-16 sm:py-24 lg:py-32 bg-forestdark text-alabaster" data-testid="credentials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">
                {t("credentials.overline")}
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight text-alabaster">
                {t("credentials.title")}
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#3A3F38] border border-[#3A3F38]">
            {credentials.map((c, i) => (
              <Reveal key={c.place} delay={0.08 * i} className="h-full">
                <div data-testid={`credential-card-${i}`} className="bg-forestdark p-8 sm:p-12 h-full relative overflow-hidden group">
                  <span className="absolute -top-6 -right-2 font-serif italic text-[7rem] leading-none text-[#2D4030] group-hover:text-[#3D523B] transition-colors duration-700 select-none">
                    {c.place}
                  </span>
                  <div className="relative">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-6">{c.place}</p>
                    <h3 className="font-serif text-xl sm:text-2xl font-medium text-alabaster mb-4">{c.title}</h3>
                    <p className="text-sm text-[#9AA096] leading-relaxed max-w-sm">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="py-16 sm:py-24 lg:py-32" data-testid="blog-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <SectionHeading overline={t("blogPreview.overline")} title={t("blogPreview.title")} />
            <Reveal delay={0.15}>
              <Link
                to="/blog"
                data-testid="blog-preview-view-all"
                className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-forest hover:text-gold transition-colors"
              >
                {t("blogPreview.viewAll")} <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>

          {articles.length === 0 ? (
            <Reveal>
              <div className="border border-[#E2E4DC] bg-mist/60 p-12 sm:p-20 text-center" data-testid="blog-preview-empty">
                <p className="font-serif italic text-2xl sm:text-3xl text-forest mb-4">{t("blogPreview.emptyTitle")}</p>
                <p className="text-sm text-[#757A70] max-w-md mx-auto leading-relaxed">
                  {t("blogPreview.emptyText")}
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={0.08 * i} className="h-full">
                  <Link
                    to={`/blog/${a.slug}`}
                    data-testid={`blog-preview-card-${i}`}
                    className="group block h-full bg-white border border-[#E2E4DC] hover:border-olive/50 transition-colors duration-500"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-sage">
                      {a.cover_image && (
                        <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      )}
                    </div>
                    <div className="p-7">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-3">
                        {t(`cat.${a.category}`)} · {formatDate(a.created_at, lang)}
                      </p>
                      <h3 className="font-serif text-xl font-medium text-ebony leading-snug group-hover:text-forest transition-colors">
                        {a.title}
                      </h3>
                      <p className="text-sm text-[#757A70] leading-relaxed mt-3 line-clamp-2">{a.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 sm:py-24 bg-sage/60 border-t border-[#E2E4DC]" data-testid="home-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="overline-label mb-6">{t("homeCta.overline")}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-ebony max-w-2xl mx-auto leading-tight">
              {t("homeCta.title")}
            </h2>
            <p className="text-base text-[#4D5248] mt-6 max-w-xl mx-auto leading-relaxed">
              {t("homeCta.text")}
            </p>
            <div className="mt-10">
              <Link to="/agendamento" data-testid="home-cta-booking-button" className="btn-primary">
                {t("homeCta.button")} <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
